import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import UnCrawlableLink from '../../../UnCrawlableLink'
import { APPLY_FDR_URL } from '../../../../../common/constants'
import Img from 'src/components/shared/Img'
import classNames from 'classnames'
import PhoneNumber from 'src/components/shared/PhoneNumber'
import PrimaryButton from 'src/components/shared/PrimaryButton'
import { useLeadId } from '../../../../../common/lead-id'
import { useRouter } from 'next/router'
import { buildQueryString } from '../../../../../common/utils'

const BottomSection = ({
  title,
  list,
  image: { src, alt },
  buttonText,
  buttonLink,
  isDark,
  showPhoneNumber,
  crawlable,
}) => {
  const leadId = useLeadId()
  const { query: baseQuery } = useRouter()

  let query = useMemo(
    () => buildQueryString(baseQuery, leadId),
    [baseQuery, leadId]
  )

  let url = buttonLink

  if (leadId) {
    url = `${url}?${query}`
  }

  return (
    <div
      className={classNames(
        'gap-x-primary relative grid grid-cols-4 px-4 lg:grid-cols-12 lg:px-0',
        { 'bg-blue-base text-white': isDark },
        { 'bg-blue-base-light': !isDark }
      )}
      data-testid="bottomSection"
    >
      <div
        id="bottomSection"
        className="col-span-4 py-6 text-center lg:col-span-5 lg:col-start-2 lg:min-h-104 lg:py-14 lg:text-left"
      >
        <h3
          id="bottomSectionTitle"
          className="max-w-514 mx-auto mb-4 text-32 font-bold leading-11 lg:text-37 lg:leading-primary-xs"
          data-testid="title"
        >
          {title}
        </h3>
        {list ? (
          <ul className="mb-7 lg:mb-4" data-testid="bottom-section-list">
            {list.map((item, i) => (
              <li
                key={i}
                className="text-lg lg:text-xl"
                data-testid="bottom-section-list-item"
              >
                {item}
              </li>
            ))}
          </ul>
        ) : null}
        {crawlable ? (
          <PrimaryButton
            isLeadCta
            id={`bottomSection`}
            sectionTitle={title}
            title={buttonText}
            className={`${
              isDark ? 'bg-sky-650 text-gray-980' : 'bg-blue-base text-white'
            } mx-auto mb-4 inline-block px-7 py-2.5`}
            href={url}
          />
        ) : (
          <UnCrawlableLink
            id={`bottomSection`}
            href={buttonLink}
            className={classNames(
              'mx-auto inline-block rounded-lg px-12 py-2.5 text-lg font-bold',
              { 'bg-sky-650 text-blue-base': isDark },
              { 'bg-blue-base text-white': !isDark }
            )}
            label={buttonText}
          />
        )}
        {showPhoneNumber ? (
          <p className="text-15" data-testid="bottom-section-final-copy">
            <PhoneNumber
              speakText="Or speak to a debt consultant "
              text="800-910-0065"
              href="tel:800-910-0065"
              isTrack={true}
              className={classNames(
                'font-bold no-underline',
                { 'text-white': isDark },
                { 'text-black-base': !isDark }
              )}
            />
          </p>
        ) : null}
      </div>
      {src ? (
        <div className="col-span-full lg:col-span-6 lg:col-start-7 xl:col-span-4">
          <div
            className="relative bottom-0 flex h-64 flex-col justify-end md:h-104 lg:absolute"
            data-testid="img"
          >
            <Img
              src={src}
              alt={alt}
              imgClassName="object-contain object-bottom lg:relative lg:object-left-bottom"
              className="size-full"
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

BottomSection.propTypes = {
  title: PropTypes.string.isRequired,
  list: PropTypes.array.isRequired,
  image: PropTypes.object.isRequired,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  isDark: PropTypes.bool,
  showPhoneNumber: PropTypes.bool,
  crawlable: PropTypes.bool,
}
BottomSection.defaultProps = {
  buttonText: 'Get your free evaluation',
  buttonLink: APPLY_FDR_URL(),
  isDark: false,
  showPhoneNumber: true,
  crawlable: true,
}

export default BottomSection
